import { ReactNode, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { QueryKey } from '@tanstack/react-query';

import { buildChunkPageMap, buildPageByAttachmentId } from 'components/shared/ai/citationPages';
import ErroredAttachmentsBanner from 'components/shared/ai/ErroredAttachmentsBanner';
import Explainer from 'components/shared/ai/Explainer';
import SourceChip from 'components/shared/ai/SourceChip';
import FadeInUp from 'components/shared/animations/FadeInUp';
import { updateJobbableCacheWithAIJob } from 'lib/aiJobHelpers';
import { simpleDate } from 'lib/date';
import Summary from 'models/aiArtifacts/Summary';
import AIJob, { AIJobOptions } from 'models/AIJob';
import Attachment from 'models/Attachment';
import { createAIArtifactExport } from 'services/api/aiArtifacts';

import { DownloadAIExportButton } from '../DownloadAIExportButton';

import SummarySection from './SummarySection';

type SummaryContentViewProps = {
  summaryJob: AIJob | null;
  summary: Summary;
  attachments: Attachment[];
  attachmentType?: string;
  queryKey: QueryKey;
  actions?: ReactNode;
};

export default function SummaryContentView({
  summaryJob,
  summary,
  attachments,
  attachmentType,
  queryKey,
  actions,
}: SummaryContentViewProps) {
  const handleCollapsePromptExplainer = useCallback(() => {
    if (!summaryJob) return;
    updateJobbableCacheWithAIJob(
      queryKey,
      new AIJob({
        ...summaryJob,
        hasCollapsedPromptExplainer: true,
      } as AIJobOptions)
    );
  }, [summaryJob, queryKey]);

  const erroredAttachments = attachments.filter((attachment) => !summaryJob?.attachmentIds.includes(attachment.id));

  const chunkPageById = useMemo(() => buildChunkPageMap(summary.data.chunks), [summary.data.chunks]);

  return (
    <FadeInUp>
      <Container>
        <ErroredAttachmentsBanner attachments={erroredAttachments} context='summary' />
        <MetadataSpan>
          Summary created by {summaryJob?.createdBy?.fullName || 'Unknown'}
          {summaryJob?.createdByGroup?.name && ` at ${summaryJob.createdByGroup.name}`} on{' '}
          {summaryJob?.createdAt ? simpleDate(summaryJob.createdAt) : 'Unknown date'}
        </MetadataSpan>
        <ActionContainer>
          {actions}
          <DownloadAIExportButton
            label='Download Summary'
            onExport={() => createAIArtifactExport(summary.id)}
            data-cy='downloadSummary'
          />
        </ActionContainer>
        <Explainer
          title="How Olio's Summary Works"
          aiPrompt={summary.aiPrompt ?? undefined}
          hasBeenCollapsed={summaryJob?.hasCollapsedPromptExplainer ?? undefined}
          onCollapseSuccess={handleCollapsePromptExplainer}
        />
        <SourcesContainer>
          <SourcesTitle>Sources ({summary.data.sources.length})</SourcesTitle>
          <CitationsContainer>
            {summary.data.sources.map((attachment) => (
              <SourceChip key={attachment.attachmentId} attachment={attachment} attachmentType={attachmentType} />
            ))}
          </CitationsContainer>
        </SourcesContainer>
        <ContentContainer>
          {summary.data.sections.map(({ title, attachmentIds, content, sources }, idx) => (
            <SummarySection
              key={`summary-section-${idx}`}
              title={title}
              attachments={attachmentIds.flatMap(
                (x) => summary.data.sources.find(({ attachmentId }) => attachmentId === x) || []
              )}
              content={content}
              attachmentType={attachmentType}
              pageByAttachmentId={buildPageByAttachmentId(sources, chunkPageById)}
            />
          ))}
        </ContentContainer>
      </Container>
    </FadeInUp>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const MetadataSpan = styled.span`
  font-style: italic;
  font-size: 12px;
  line-height: 12px;
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const SourcesContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
`;

const SourcesTitle = styled.h2`
  font-weight: 700;
  font-size: 14px;
  line-height: 12px;
`;

const CitationsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
